import { useEffect, useRef } from "react";
import { useDebounce } from "@uidotdev/usehooks";

import useTill from "../data/useTill";
import { ITillQuoteRequest } from "../models/till/ITillQuote";

interface UseReaderDisplaySyncOptions {
  readerId: string | null | undefined;
  quoteRequest: ITillQuoteRequest;
  basketSignature: string;
  enabled: boolean;
}

/**
 * Mirrors the basket onto the reader's screen while a sale is being built, debounced so a burst
 * of scans is one request. Clears the screen when the reader changes or the till leaves selling.
 */
export default function useReaderDisplaySync({ readerId, quoteRequest, basketSignature, enabled }: UseReaderDisplaySyncOptions) {
  const { displayOnReader, clearReaderDisplay } = useTill("useReaderDisplaySync");
  const debouncedSignature = useDebounce(`${basketSignature}|${JSON.stringify(quoteRequest)}`, 600);
  const lastReaderRef = useRef<string | null>(null);

  // Mutations are stable per render but not referentially; keep the latest without re-running effects.
  const displayRef = useRef(displayOnReader.mutate);
  const clearRef = useRef(clearReaderDisplay.mutate);
  displayRef.current = displayOnReader.mutate;
  clearRef.current = clearReaderDisplay.mutate;

  useEffect(() => {
    if (!enabled || !readerId) return;
    lastReaderRef.current = readerId;
    displayRef.current({ readerId, body: quoteRequest });
    // quoteRequest is folded into debouncedSignature.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, readerId, debouncedSignature]);

  useEffect(() => {
    return () => {
      if (lastReaderRef.current) clearRef.current(lastReaderRef.current);
    };
  }, []);

  useEffect(() => {
    if (enabled) return;
    if (lastReaderRef.current) {
      clearRef.current(lastReaderRef.current);
      lastReaderRef.current = null;
    }
  }, [enabled]);
}
